/* eslint-disable rxjs-angular/prefer-takeuntil */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  type OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  CHAR_MINIMAL_FOR_SEARCH,
  SEARCH_RESULTS_URL,
  SearchService,
} from '@search/services';
import { KeyboardNavigationDirective } from '@core/accessibility';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { debounceTime, filter, Subject } from 'rxjs';
import { DisableBodyScrollingClassDirective } from '@innogy/utils-dom';

import { SearchPopoverComponent } from '../search-popover/search-popover.component';

@Component({
  selector: 'wl-search-bar',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    FormsModule,
    SearchPopoverComponent,
    KeyboardNavigationDirective,
    AutomationIdDirective,
    DisableBodyScrollingClassDirective,
  ],
  templateUrl: './search-bar.component.html',
  styleUrls: [
    './search-bar.component.essent.scss',
    './search-bar.component.ed.scss',
  ],
})
export class SearchBarComponent implements OnInit, AfterViewInit {
  readonly basePath = inject(ENVIRONMENT_CONFIG).basePath;
  readonly autofocus = input(false);
  readonly inheritQueryFromParams = input(false);
  removedFocus = output();
  readonly searchQuery = signal('');

  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild(SearchPopoverComponent) popover!: SearchPopoverComponent;

  readonly #router = inject(Router);
  readonly #focus$ = new Subject<boolean>();
  elementHasFocus$ = this.#focus$.pipe(debounceTime(150));
  readonly #searchService = inject(SearchService);
  readonly #destroyRef = inject(DestroyRef);

  @HostListener('focusin', [])
  applyFocus() {
    this.#focus$.next(true);
  }

  @HostListener('document:keydown.escape', [])
  @HostListener('focusout', [])
  removeFocus() {
    this.#focus$.next(false);
  }

  async ngOnInit() {
    if (this.inheritQueryFromParams()) {
      await this.#useQueryFromParams();
    }

    this.#listenToFocusChanges();
  }

  async ngAfterViewInit() {
    if (this.autofocus()) {
      this.searchInput.nativeElement.focus();
    }
  }

  submit(event: Event) {
    event.preventDefault();
    if (this.searchQuery().length >= CHAR_MINIMAL_FOR_SEARCH) {
      this.#router.navigate([this.basePath + SEARCH_RESULTS_URL], {
        queryParams: { q: this.searchQuery() },
      });
    }
  }

  async #useQueryFromParams() {
    const result = await this.#searchService.getSearchQueryFromParams();
    this.searchQuery.set(result);
  }

  #listenToFocusChanges() {
    this.elementHasFocus$
      .pipe(
        filter((focus) => !focus),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.removedFocus.emit());
  }
}
